import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Tour } from '../../types/tours';
import { Link } from 'react-router-dom';
import { Info, CalendarRange } from 'lucide-react';

interface RelatedToursProps {
  currentTourId: string;
}

const RelatedTours: React.FC<RelatedToursProps> = ({ currentTourId }) => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelatedTours = async () => {
      try {
        const { data, error } = await supabase
          .from('tours')
          .select(`
            *,
            tour_images (
              id,
              image_url,
              image_type,
              display_order
            )
          `)
          .neq('id', currentTourId)
          .eq('status', 'published')
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) throw error;
        setTours(data || []);
      } catch (err) {
        console.error('Error fetching related tours:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedTours();
  }, [currentTourId]);

  if (loading) return <div>Loading...</div>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {tours.map((tour) => {
        const carouselImage = tour.tour_images
          ?.find(img => img.image_type === 'carousel' && img.display_order === 0)
          ?.image_url;

        const lowestPrice = Math.min(
          tour.group_tour_price,
          tour.reduced_group_price || Number.MAX_VALUE,
          tour.private_price || Number.MAX_VALUE
        );

        return (
          <div
            key={tour.id}
            className="group bg-[#F2F3F4] rounded-xl overflow-hidden hover:shadow-lg transition-all flex flex-col"
          >
            <div className="relative aspect-[4/3] overflow-hidden">
              <img
                src={carouselImage}
                alt={tour.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute top-4 right-4 bg-[#953B0E] px-3 py-1 rounded-full text-sm font-semibold text-white border-2 border-[#E85C0D]">
                From ${lowestPrice}
              </div>
            </div>

            <div className="p-6 flex flex-col flex-1">
              <h3 className="text-xl font-semibold text-[#2A382A] mb-2">
                {tour.name}
              </h3>
              <p className="text-[#2A382A]/70 text-sm mb-auto">
                {tour.tour_card_description}
              </p>

              <div className="flex gap-3 mt-4">
                <Link
                  to={`/tours/${tour.slug}`}
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-white text-[#2A382A] rounded-lg hover:bg-[#2A382A]/5 transition-colors"
                >
                  <Info className="w-4 h-4" />
                  Details
                </Link>
                <Link
                  to={`/tours/${tour.slug}/book`}
                  className="flex-1 flex items-center justify-center gap-2 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors border-2 border-[#E85C0D]"
                >
                  <CalendarRange className="w-4 h-4" />
                  Book Now
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RelatedTours;